import React from 'react';
import { Helmet } from 'react-helmet-async';
import { MapPin, Phone, Calendar, ArrowRight, CheckCircle, Clock, Car, Heart, Brain, Moon, Video, Briefcase } from 'lucide-react';
import CTA from '../../components/CTA';

const LimavadyAnxietyStress: React.FC = () => {
  return (
    <main>
      <Helmet>
        <title>Anxiety Treatment Limavady | Professional Stress Hypnotherapy Efficiency</title>
        <meta
          name="description"
          content="Professional anxiety and stress hypnotherapy serving Limavady. Online sessions recommended for convenience. Business stress, work-life balance, anxiety treatment. CNHC registered."
        />
        <meta name="keywords" content="anxiety treatment Limavady, stress management Limavady, business stress North West, hypnotherapy Limavady County Londonderry, work-life balance anxiety, online therapy Limavady" />
        <link rel="canonical" href="https://www.sharonclare-hypnotherapy.com/limavady-anxiety-stress/" />
        <meta property="og:title" content="Anxiety Treatment Limavady | Professional Stress Hypnotherapy Efficiency" />
        <meta property="og:description" content="Professional anxiety and stress hypnotherapy serving Limavady. Business stress, work-life balance, anxiety treatment. Online for efficiency or short 14-mile drive." />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "name": "Sharon Clare Hypnotherapy - Anxiety and Stress Treatment Limavady",
            "description": "CNHC registered hypnotherapy for anxiety and stress, serving Limavady via online sessions and Belfast clinic. Specializing in business stress, entrepreneurial pressure, work-life balance anxiety, anxiety disorders, and supporting North West professionals.",
            "url": "https://www.sharonclare-hypnotherapy.com/limavady-anxiety-stress/",
            "telephone": "07932139245",
            "email": "Sharon@sharonclare-hypnotherapy.com",
            "image": "https://www.sharonclare-hypnotherapy.com/images/sharon-clare.jpg",
            "serviceType": "Anxiety and Stress Treatment",
            "areaServed": {
              "@type": "City",
              "name": "Limavady",
              "containedInPlace": {
                "@type": "AdministrativeArea",
                "name": "Northern Ireland"
              }
            },
            "location": [
              {
                "@type": "Place",
                "name": "Belfast Clinic",
                "address": {
                  "@type": "PostalAddress",
                  "addressLocality": "Belfast",
                  "addressRegion": "County Down",
                  "postalCode": "BT7 2FZ",
                  "addressCountry": "GB"
                }
              }
            ],
            "sameAs": [
              "https://instagram.com/sharonclarehypnotherapy",
              "https://www.facebook.com/SharonClareHypnotherapy/",
              "https://www.linkedin.com/in/sharonclare-hypnotherapy"
            ]
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "How far is Limavady from your Belfast clinic for anxiety treatment?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Limavady is just approximately 60 miles from the Belfast clinic. This short distance makes in-person sessions very practical. However, for busy professionals, online sessions save approximately 42 minutes of travel time per appointment (21 minutes each way), which can be significant when balancing work demands, business commitments, and family responsibilities."
                }
              },
              {
                "@type": "Question",
                "name": "Can I do anxiety treatment online to save time?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, absolutely. Online sessions are recommended for Limavady professionals who want to maximize efficiency. While the Belfast clinic is some distance away, online hypnotherapy saves significant travel time and eliminates travel logistics. Online sessions provide exactly the same professional, evidence-based anxiety treatment with the convenience of attending from home or office. This is ideal for busy business owners, food industry professionals, and anyone prioritizing time efficiency."
                }
              },
              {
                "@type": "Question",
                "name": "Does hypnotherapy work for business stress and entrepreneurial anxiety?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, solution-focused hypnotherapy is highly effective for business-related stress and entrepreneurial anxiety. Many Limavady business owners and professionals deal with financial pressures, decision-making stress, work-life balance challenges, and the constant mental load of running a business. Hypnotherapy helps by calming the stress response, building resilience, improving sleep quality, developing healthier boundaries, and creating sustainable strategies for managing business pressures."
                }
              },
              {
                "@type": "Question",
                "name": "Can hypnotherapy help with work-life balance anxiety?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, hypnotherapy is very effective for anxiety related to work-life balance challenges. Many Limavady professionals struggle with the demands of business ownership, food industry pressures, long working hours, and finding time for family and self-care. Solution-focused hypnotherapy helps you manage stress, set healthier boundaries, build resilience, and create a more sustainable relationship with work. You learn to switch off, prioritize effectively, and protect your wellbeing without sacrificing professional success."
                }
              },
              {
                "@type": "Question",
                "name": "How long does anxiety treatment take?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Most clients notice significant improvements within 4-6 sessions for anxiety and stress. Some people feel calmer and more in control after just one or two sessions. The number varies based on your individual circumstances, the severity of symptoms, and your goals. We'll discuss your specific needs during the free initial consultation and create a tailored treatment plan. I offer a discounted 4-session package which provides better value and ensures commitment to the process."
                }
              },
              {
                "@type": "Question",
                "name": "Is evening availability offered for busy professionals?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, I offer flexible appointment times including early morning, daytime, and evening slots to accommodate busy professionals and business owners. Whether you choose in-person sessions at the Belfast clinic or online sessions from your home or office, I work with your schedule. Many Limavady professionals prefer online evening sessions for maximum convenience and time efficiency after a full work day."
                }
              }
            ]
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-gray-900 mb-6">
              Anxiety and Stress Treatment for Limavady
            </h1>
            <p className="text-xl sm:text-2xl text-gray-600 mb-6 leading-relaxed">
              Professional hypnotherapy for North West market town professionals - time-efficient solutions
            </p>
            <div className="bg-white rounded-xl p-6 mb-8 inline-block">
              <div className="flex items-center justify-center space-x-3 mb-3">
                <MapPin className="w-6 h-6 text-accent-600" />
                <p className="text-lg text-gray-700 font-semibold">
                  In person at the Belfast clinic on the Ormeau Road or the Lisburn clinic at The Arzen Collective
                </p>
              </div>
              <div className="flex items-center justify-center space-x-3 text-gray-600">
                <Video className="w-5 h-5" />
                <p className="text-base">
                  Online sessions also available any day
                </p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/book"
                className="btn-primary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                <Calendar className="w-5 h-5 mr-2" />
                Book Free Consultation
              </a>
              <a
                href="tel:07932139245"
                className="btn-secondary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                <Phone className="w-5 h-5 mr-2" />
                Call 07932 139245
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Understanding Anxiety in Limavady */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6">
              Understanding Anxiety and Stress in Limavady
            </h2>
            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
              <p>
                If you're living in Limavady and struggling with anxiety or stress, you're not alone. With a population of around 11,000, Limavady functions as a market town and commercial center for the North West, with significant food processing and retail sectors driving the local economy. This business-focused character creates unique pressures for professionals, business owners, and working families - time poverty, work-life balance challenges, entrepreneurial stress, and the constant mental load of juggling competing demands.
              </p>
              <p>
                Many Limavady residents experience anxiety related to business pressures and professional stress. Whether you're running a small business competing with larger regional centers and online retail, managing staff in the food processing sector, or working long hours in hospitality and retail, the demands can be relentless. There's financial uncertainty, decision-making pressure, the responsibility of keeping businesses going or meeting performance targets, and the exhausting challenge of being "on" all the time. The mental load never stops - even when you're at home, you're still thinking about work, checking emails, worrying about tomorrow's challenges.
              </p>
              <p>
                For many Limavady professionals and families, work-life balance has become work-life imbalance. You're constantly rushing - leaving for work early, getting home late, missing family time, feeling guilty about not being present with your children or partner, struggling to find any time for yourself. The anxiety builds: Am I doing enough at work? Am I being a good enough parent? When did I last do something just for me? The physical symptoms follow - racing heart, tight chest, poor sleep despite exhaustion, constant tension. It feels like you're running on empty, but you can't slow down.
              </p>
              <p>
                The good news is that professional anxiety treatment is accessible from Limavady. My Belfast clinic is just 14 miles from Limavady via the A37 - only 21 minutes. This short distance makes in-person sessions very practical for regular appointments. However, for busy professionals prioritizing time efficiency, online sessions offer significant advantages: you save approximately 42 minutes per session (21 minutes each way), eliminate travel logistics, and can attend from the comfort of your home or office. Online hypnotherapy provides exactly the same professional, evidence-based anxiety treatment - only the location changes. For time-conscious Limavady professionals, this efficiency can make the difference between fitting therapy into your schedule or not seeking help at all.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How Anxiety Affects You */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8 text-center">
              How Anxiety and Stress Affect Limavady Professionals
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
                <h3 className="font-heading font-semibold text-gray-900 mb-4 text-xl">Common Anxiety Symptoms</h3>
                <ul className="space-y-3">
                  {[
                    'Difficulty falling or staying asleep',
                    'Racing thoughts, especially at night',
                    'Physical symptoms: tight chest, pounding heart, tense shoulders',
                    'Constant sense of unease or dread',
                    'Avoiding situations out of fear or worry',
                    'Feeling overwhelmed by everyday tasks',
                    'Difficulty concentrating or making decisions',
                    'Irritability and mood swings'
                  ].map((item) => (
                    <li key={item} className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
                <h3 className="font-heading font-semibold text-gray-900 mb-4 text-xl">Limavady-Specific Stressors</h3>
                <ul className="space-y-3">
                  {[
                    'Business ownership stress and pressure',
                    'Food industry demands and deadlines',
                    'Entrepreneurial financial uncertainty',
                    'Work-life balance and time poverty',
                    'Long working hours in retail/hospitality',
                    'Decision-making pressure and responsibility',
                    'Professional performance anxiety',
                    'Family guilt from work commitments'
                  ].map((item) => (
                    <li key={item} className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-accent-600 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-8 mt-6">
              <p className="text-gray-700 text-lg">
                When anxiety and stress dominate your professional life, it affects everything - your sleep, your relationships, your health, your ability to enjoy success or switch off from work pressures. The constant worry, the physical tension, the exhaustion from being on high alert all the time - it's unsustainable. But you don't have to live like this. Solution-focused hypnotherapy offers an evidence-based approach that addresses both the symptoms and the underlying causes of anxiety, helping you regain control, set healthier boundaries, and build lasting calm while maintaining your professional effectiveness.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Understanding Anxiety Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6">
              Understanding Anxiety: What's Really Happening
            </h2>
            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
              <p>
                Anxiety is incredibly common among professionals and business owners - around 1 in 4 people in the UK will experience an anxiety disorder at some point in their lives. The pressures of professional life - financial responsibility, decision-making stress, performance expectations, work-life balance challenges - can trigger and maintain chronic anxiety. Anxiety is your brain's natural alarm system going into overdrive, interpreting everyday work situations - difficult conversations, financial decisions, deadlines, staff issues - as serious threats and triggering your fight-or-flight response.
              </p>
              <p>
                When you're anxious, your brain releases stress hormones like cortisol and adrenaline, preparing your body to respond to a perceived threat. This is why you experience physical symptoms - racing heart, tight chest, shallow breathing, muscle tension, digestive issues, feeling on edge. Your brain is literally preparing you to fight or run away, even when you're just sitting at your desk checking emails or lying in bed worrying about tomorrow's challenges.
              </p>
              <p>
                For professionals and business owners, anxiety often manifests as: generalized anxiety disorder (persistent, excessive worry about business, finances, family, health), performance anxiety (fear of failure, not measuring up, making mistakes), decision-making paralysis (overthinking every choice, unable to commit), and burnout (physical and emotional exhaustion from chronic stress). The line between "healthy stress" and "anxiety disorder" becomes blurred when work stress starts significantly impacting your sleep, relationships, health, and quality of life.
              </p>
              <p>
                The good news is that anxiety is highly treatable, even for busy professionals who struggle to find time for self-care. Solution-focused hypnotherapy is specifically designed for time-conscious clients - sessions are focused and efficient, online options eliminate travel time, and the approach is practical rather than endlessly analytical. Your brain created these anxious patterns through repeated stress and overwork, and your brain can learn new, healthier patterns. You can learn to set boundaries, switch off, manage stress effectively, and protect your wellbeing without sacrificing professional success.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How Hypnotherapy Helps */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8 text-center">
              How Hypnotherapy Helps with Anxiety and Stress
            </h2>
            <p className="text-gray-700 leading-relaxed mb-8 text-center max-w-3xl mx-auto">
              Solution-focused hypnotherapy gently shifts focus away from worry and toward practical, positive change. Hypnosis calms the overactive fight-or-flight response so panic and tension reduce, and clarity returns. It's not about dwelling on the past or endlessly analyzing problems - it's about building the resilience, boundaries, and calm you need to move forward professionally and personally.
            </p>
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              {[
                {
                  icon: <Heart className="w-8 h-8 text-accent-600" />,
                  title: 'Calm the Nervous System',
                  text: 'Soothe the overactive stress response and reduce physical anxiety symptoms like racing heart, tight chest, and tense muscles. Your body learns to relax again, even during busy business periods or challenging work situations.'
                },
                {
                  icon: <Brain className="w-8 h-8 text-accent-600" />,
                  title: 'Rewire Thought Patterns',
                  text: 'Build new habits of thought and behavior that support resilience and sustainable performance. Replace anxious, overwhelmed thinking with calmer, more strategic responses to business pressures and professional challenges.'
                },
                {
                  icon: <Moon className="w-8 h-8 text-accent-600" />,
                  title: 'Restore Restful Sleep',
                  text: 'Create a calmer pre-sleep routine and quiet the racing mind that keeps reviewing work problems at 3am. Wake feeling more refreshed and better equipped to handle the demands of professional life with energy and focus.'
                }
              ].map(({ icon, title, text }) => (
                <div key={title} className="bg-white rounded-2xl p-8">
                  <div className="flex flex-col items-center text-center">
                    <div className="mb-4">{icon}</div>
                    <h3 className="font-heading font-semibold text-gray-900 mb-3 text-xl">{title}</h3>
                    <p className="text-gray-700">{text}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-white rounded-2xl p-8">
              <h3 className="font-heading font-semibold text-gray-900 mb-4 text-xl text-center">What Hypnotherapy Can Help With</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <ul className="space-y-2">
                  {[
                    'Generalized anxiety disorder',
                    'Performance anxiety and imposter syndrome',
                    'Panic attacks and acute stress',
                    'Business stress and entrepreneurial pressure',
                    'Decision-making anxiety and paralysis'
                  ].map((item) => (
                    <li key={item} className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
                <ul className="space-y-2">
                  {[
                    'Work-life balance anxiety and guilt',
                    'Professional burnout and exhaustion',
                    'Financial anxiety and uncertainty',
                    'Sleep problems from racing thoughts',
                    'Relationship strain from work stress'
                  ].map((item) => (
                    <li key={item} className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sharon's Approach */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6">
              Sharon's Approach to Anxiety Treatment
            </h2>
            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
              <p>
                As a CNHC registered clinical hypnotherapist with 15 years of nursing experience at Belfast City Hospital, I bring professional healthcare expertise to every anxiety treatment session. My background in nursing means I understand the medical and neurological side of anxiety - the brain chemistry, the physical symptoms, the impact of chronic stress on health. But more importantly, I understand the human side for busy professionals: the exhaustion, the guilt, the pressure to keep performing, and the courage it takes to admit you need help when you're supposed to "have it all together."
              </p>
              <p>
                I use solution-focused hypnotherapy, an evidence-based approach that combines talking therapy with hypnosis. Each session begins with structured conversation about your week, focusing on what's working, what you want to achieve, and practical steps forward. This isn't about dwelling on problems or endlessly analyzing sources of stress - it's about building momentum toward positive change and identifying what you can control. For time-conscious professionals, this efficiency is valuable. Then we move into hypnosis, a deeply relaxed state where your subconscious mind becomes more receptive to positive suggestions and new patterns of thinking.
              </p>
              <p>
                Hypnosis isn't sleep, and it's not stage hypnosis. You remain completely aware and in control throughout. It's simply a state of focused attention and deep relaxation - similar to being absorbed in a good book or driving a familiar route on autopilot. In this relaxed state, we work with your subconscious mind to calm the overactive anxiety response, build resilience to professional pressures, set healthier boundaries, and create lasting change in how you think about and respond to work stress.
              </p>
              <p>
                Most clients notice significant improvements within 4-6 sessions, though everyone's journey is different. Some professionals feel noticeably calmer and more in control after just one or two sessions. Others need a bit more time to build lasting change, especially if anxiety and burnout have been building for years. What matters is that you're moving forward at your own pace, supported by evidence-based treatment that addresses both the symptoms and the underlying causes of professional anxiety. You don't have to sacrifice your health and wellbeing for professional success. Help is available, time-efficient, and effective.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Getting There Section */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8 text-center">
              Accessing Anxiety Treatment from Limavady
            </h2>

            <div className="bg-white rounded-2xl p-8 mb-8 border-2 border-accent-200">
              <div className="flex items-start space-x-4 mb-6">
                <Video className="w-8 h-8 text-accent-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2 text-lg">Online Sessions (Recommended for Efficiency)</h3>
                  <p className="text-gray-700 mb-4">
                    For busy Limavady professionals, online sessions offer significant time savings and convenience:
                  </p>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                      <span><span className="font-semibold">Save 42 minutes per session</span> - eliminate 21 minutes each way of travel time</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                      <span><span className="font-semibold">Flexible scheduling</span> - fit therapy around business commitments, meetings, and irregular hours</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                      <span><span className="font-semibold">Complete convenience</span> - attend from home or office without travel logistics</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                      <span><span className="font-semibold">Same professional treatment</span> - evidence-based hypnotherapy, only the location changes</span>
                    </li>
                  </ul>
                </div>
              </div>
              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-6">
                <p className="text-gray-700">
                  Online sessions use secure video call technology. All you need is a quiet space (home or office), a stable internet connection, and a device with a camera. For time-conscious professionals who struggle to fit self-care into busy schedules, saving 42 minutes per session can be the difference between seeking help and continuing to struggle with anxiety and burnout.
                </p>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-8">
              <div className="flex items-start space-x-4">
                <Car className="w-8 h-8 text-accent-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2 text-lg">In-Person at Belfast Clinic</h3>
                  <p className="text-gray-700 mb-4">
                    If you prefer face-to-face treatment, in-person sessions are available at the Belfast clinic:
                  </p>
                  <p className="text-gray-700 mb-2">
                    <span className="font-semibold">Address:</span> Belfast Wellness Clinic, 228 Ormeau Road, Belfast BT7 2FZ
                  </p>
                  <p className="text-gray-700 mb-2">
                    <span className="font-semibold">Distance:</span> Approximately 60 miles from Limavady
                  </p>
                  <p className="text-gray-700">
                    <span className="font-semibold">Note:</span> Most Limavady clients choose online sessions for convenience
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-12 text-center">
              What North West Area Clients Say
            </h2>
            <div className="space-y-6">
              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-2xl p-8 shadow-lg">
                <p className="text-lg text-gray-700 italic mb-4">
                  "Running a business, I was completely burnt out. Working 70-hour weeks, constant worry about finances and staff, terrible sleep, snapping at my family. I couldn't keep going like this. Sharon's online hypnotherapy was perfect - I could fit it around my business schedule without the drive. After five sessions, I'm sleeping properly, I can switch off in the evenings, and I've learned to set boundaries. The business stress is still there, but I handle it completely differently now. I can actually enjoy my success instead of just worrying about losing it."
                </p>
                <p className="font-semibold text-gray-900">M.P., North West - Business Stress and Burnout</p>
              </div>

              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-2xl p-8 shadow-lg">
                <p className="text-lg text-gray-700 italic mb-4">
                  "The panic attacks started at work - racing heart, couldn't breathe, convinced I was having a heart attack. I was terrified it would happen in front of customers or staff. Sharon helped me understand what was happening and gave me tools to manage it. After six sessions, the panic attacks have stopped, I can handle work pressure without falling apart, and I feel like I've got control back. The online format meant I could do sessions early morning before work, which made it sustainable for me."
                </p>
                <p className="font-semibold text-gray-900">S.H., County Londonderry - Panic Attacks and Work Anxiety</p>
              </div>

              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-2xl p-8 shadow-lg">
                <p className="text-lg text-gray-700 italic mb-4">
                  "Between work demands, trying to be present for my kids, and feeling guilty all the time about never doing enough - I was exhausted and anxious constantly. Couldn't sleep, couldn't relax, always on edge. Sharon's approach helped me manage the stress, set realistic expectations, and actually switch off when I'm home. I'm still juggling the same responsibilities, but I cope so much better. The anxiety doesn't control me anymore. Being able to do evening sessions online made it actually possible to commit to the process."
                </p>
                <p className="font-semibold text-gray-900">K.D., North West - Work-Life Balance and Parenting Anxiety</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-12 text-center">
              Frequently Asked Questions - Limavady Anxiety Treatment
            </h2>
            <div className="space-y-6">
              <div className="bg-white rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  How far is Limavady from your Belfast clinic for anxiety treatment?
                </h3>
                <p className="text-gray-700">
                  Limavady is just approximately 60 miles from the Belfast clinic. This short distance makes in-person sessions very practical. However, for busy professionals, online sessions save approximately 42 minutes of travel time per appointment (21 minutes each way), which can be significant when balancing work demands, business commitments, and family responsibilities.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Can I do anxiety treatment online to save time?
                </h3>
                <p className="text-gray-700">
                  Yes, absolutely. Online sessions are recommended for Limavady professionals who want to maximize efficiency. While the Belfast clinic is some distance away, online hypnotherapy saves significant travel time and eliminates travel logistics. Online sessions provide exactly the same professional, evidence-based anxiety treatment with the convenience of attending from home or office. This is ideal for busy business owners, food industry professionals, and anyone prioritizing time efficiency.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Does hypnotherapy work for business stress and entrepreneurial anxiety?
                </h3>
                <p className="text-gray-700">
                  Yes, solution-focused hypnotherapy is highly effective for business-related stress and entrepreneurial anxiety. Many Limavady business owners and professionals deal with financial pressures, decision-making stress, work-life balance challenges, and the constant mental load of running a business. Hypnotherapy helps by calming the stress response, building resilience, improving sleep quality, developing healthier boundaries, and creating sustainable strategies for managing business pressures.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Can hypnotherapy help with work-life balance anxiety?
                </h3>
                <p className="text-gray-700">
                  Yes, hypnotherapy is very effective for anxiety related to work-life balance challenges. Many Limavady professionals struggle with the demands of business ownership, food industry pressures, long working hours, and finding time for family and self-care. Solution-focused hypnotherapy helps you manage stress, set healthier boundaries, build resilience, and create a more sustainable relationship with work. You learn to switch off, prioritize effectively, and protect your wellbeing without sacrificing professional success.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  How long does anxiety treatment take?
                </h3>
                <p className="text-gray-700">
                  Most clients notice significant improvements within 4-6 sessions for anxiety and stress. Some people feel calmer and more in control after just one or two sessions. The number varies based on your individual circumstances, the severity of symptoms, and your goals. We'll discuss your specific needs during the free initial consultation and create a tailored treatment plan. I offer a discounted 4-session package which provides better value and ensures commitment to the process.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Is evening availability offered for busy professionals?
                </h3>
                <p className="text-gray-700">
                  Yes, I offer flexible appointment times including early morning, daytime, and evening slots to accommodate busy professionals and business owners. Whether you choose in-person sessions at the Belfast clinic or online sessions from your home or office, I work with your schedule. Many Limavady professionals prefer online evening sessions for maximum convenience and time efficiency after a full work day.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <CTA
        title="Start Your Journey to Calm"
        subtitle="Take the first step toward less anxiety, better work-life balance, and sustainable professional success. Book a free consultation today - online sessions available for maximum efficiency."
        primaryButtonText="Book Free Consultation"
        primaryButtonHref="/book"
        secondaryButtonText="Call Now"
        secondaryButtonHref="tel:07932139245"
        variant="accent"
      />
    </main>
  );
};

export default LimavadyAnxietyStress;
